import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Cards } from '../components/Cards';
import ScrollAnimation from '../components/ScrollAnimation';
import attractionsData from '../data/attractions.json';
import { fadeInUp, fadeInLeft, fadeInRight, staggerContainer, popIn } from '../utils/animations';
import './css/home.css';

// Import images explicitly to ensure they're processed by Vite
import edinburghCastleImg from '../assets/edinburgh_castle.jpg';
import ghostToursImg from '../assets/ghost_tours.jpg';
import princessStreetGardensImg from '../assets/princess_street_gardens.jpg';
import scotchWhiskyExperienceImg from '../assets/scotch_whisky_experience.jpg';
import royalMileImg from '../assets/royal_mile.jpg';

const Home = () => {
  // Reference to sections
  const cardsRef = useRef(null);
  const heroRef = useRef(null);
  const cardsSecRef = useRef(null);
  const interactiveRef = useRef(null);
  const highlightsRef = useRef(null);
  
  // State for mouse position (for parallax effect)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  // Track visibility states
  const [visibleSections, setVisibleSections] = useState({
    hero: true, // Hero is visible by default
    cards: false,
    interactive: false,
    highlights: false
  });
  
  // Track if we're on mobile view
  const [isMobile, setIsMobile] = useState(false);
  
  // Add scroll progress state for animations
  const [scrollProgress, setScrollProgress] = useState(0);
  
  // Setup mouse move handler for parallax effects
  useEffect(() => {
    const handleMouseMove = (e) => {
      // Calculate mouse position as percentage of screen dimensions
      const x = (e.clientX / window.innerWidth) - 0.5;
      const y = (e.clientY / window.innerHeight) - 0.5;
      
      setMousePosition({ x, y });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);
  
  // Check screen size for mobile/desktop layout
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1230);
    };
    
    // Check initially
    checkMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkMobile);
    
    // Clean up
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Advanced scroll tracking with progress percentage
  useEffect(() => {
    const handleScroll = () => {
      // More reliable detection function
      const isInViewport = (element, threshold = 0.3) => {
        if (!element) return false;
        
        const rect = element.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        
        // Element is considered visible when certain percentage is in viewport
        const visibleHeight = Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
        const elementHeight = rect.bottom - rect.top;
        
        return visibleHeight > 0 && (visibleHeight / elementHeight) > threshold;
      };
      
      // Update visibility states
      setVisibleSections({
        hero: isInViewport(heroRef.current),
        cards: isInViewport(cardsSecRef.current, 0.2),
        interactive: isInViewport(interactiveRef.current, 0.2),
        highlights: isInViewport(highlightsRef.current, 0.2)
      });
      
      // Calculate scroll progress percentage (0-100)
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / totalHeight) * 100;
      setScrollProgress(progress);
    };
    
    // Set initial state
    handleScroll();
    
    // Add scroll event listener with passive flag for performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Clean up
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Scroll to cards section with enhanced animation
  const scrollToCards = () => {
    if (cardsRef.current) {
      // Get the current scroll position
      const startPosition = window.scrollY;
      // Get the target position
      const targetPosition = cardsRef.current.offsetTop;
      // Calculate the distance to scroll
      const distance = targetPosition - startPosition;
      // Duration in milliseconds
      const duration = 1200;
      // Start time
      const startTime = performance.now();
      
      // Easing function (ease out cubic)
      const easeOutCubic = (t) => {
        return 1 - Math.pow(1 - t, 3);
      };
      
      // Animation function
      const animateScroll = (timestamp) => {
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = easeOutCubic(progress);
        
        window.scrollTo(0, startPosition + distance * eased);
        
        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };
      
      // Start the animation
      requestAnimationFrame(animateScroll);
    }
  };

  // Hero section animation variants
  const heroTextVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  
  // Title animation with staggered letters
  const titleText = "Welcome to Edinburgh";
  const titleLetters = titleText.split("");
  
  return (
    <>
      {/* Hero Section with parallax effect and Edinburgh cityscape */}
      <div 
        ref={heroRef}
        className={`hero-section ${visibleSections.hero ? 'visible' : ''}`}
        style={{
          '--scroll-progress': `${scrollProgress}%`,
          backgroundPositionY: `${50 - (mousePosition.y * 10)}%`,
          backgroundPositionX: `${50 - (mousePosition.x * 10)}%`
        }}
      >
        <div className="hero-overlay"></div>
        
        <motion.div 
          className="container hero-content"
          style={{
            transform: `perspective(1000px) 
                       translateZ(${mousePosition.y * 20}px) 
                       translateX(${mousePosition.x * -10}px)
                       rotateX(${mousePosition.y * 2}deg)
                       rotateY(${mousePosition.x * -2}deg)`
          }}
        >
          <div className="hero-title-container">
            {titleLetters.map((letter, i) => (
              <motion.span
                key={i}
                className="hero-title-letter"
                initial={{ opacity: 0, y: -20 }}
                animate={{ 
                  opacity: 1, 
                  y: 0, 
                  transition: { 
                    delay: 0.2 + (i * 0.05),
                    duration: 0.8,
                    ease: [0.16, 1, 0.3, 1]
                  }
                }}
              >
                {letter === ' ' ? '\u00A0' : letter}
              </motion.span>
            ))}
          </div>
          
          <motion.p
            initial="hidden"
            animate="visible"
            variants={heroTextVariants}
            transition={{ delay: 0.8 }}
          >
            Discover, Explore, Relax
          </motion.p>
          
          <motion.p
            initial="hidden"
            animate="visible"
            variants={heroTextVariants}
            transition={{ delay: 1.0 }}
          >
            With Visit Edinburgh
          </motion.p>
          
          <motion.button 
            className="scroll-button"
            onClick={scrollToCards}
            initial={{ opacity: 0, y: 20 }}
            animate={{ 
              opacity: 1, 
              y: 0,
              transition: {
                delay: 1.3,
                duration: 0.7,
                ease: [0.16, 1, 0.3, 1]
              }
            }}
            whileHover={{ 
              scale: 1.05,
              boxShadow: "0 8px 25px rgba(212, 175, 55, 0.5)"
            }}
            whileTap={{ 
              scale: 0.95,
            }}
          >
            Explore Edinburgh
          </motion.button>
        </motion.div>
        
        <div className="scroll-indicator">
          <div className="mouse">
            <div className="mouse-wheel"></div>
          </div>
          <div className="scroll-text">Scroll to Discover</div>
        </div>
      </div>

      {/* Cards Section with enhanced interactions */}
      <section 
        ref={cardsSecRef}
        className={`cards-section ${visibleSections.cards ? 'visible' : ''} ${isMobile ? 'mobile-view' : ''}`}
        style={{ 
          '--scroll-progress': `${scrollProgress}%`,
          '--mouse-x': mousePosition.x,
          '--mouse-y': mousePosition.y
        }}
      >
        <div className="cards-background-decoration"></div>
        <div className="container" ref={cardsRef}>
          <div className="cards-content">
            <ScrollAnimation variants={fadeInUp}>
              <h3 className="section-title">
                <span className="section-title-text">Explore Edinburgh</span>
                <span className="section-title-underline"></span>
              </h3>
            </ScrollAnimation>
            
            <Cards 
              attractions={attractionsData}
              isVisible={visibleSections.cards}
            />
          </div>
        </div>
      </section>

      {/* Interactive Section with enhanced effects */}
      <section 
        ref={interactiveRef}
        className={`interactive-section ${visibleSections.interactive ? 'visible' : ''}`}
      >
        <div className="interactive-background-particles"></div>
        <div className="container">
          <motion.div 
            className="interactive-content"
            style={{
              transform: `perspective(1000px) 
                       translateZ(${mousePosition.y * 10}px) 
                       translateX(${mousePosition.x * -5}px)`
            }}
          >
            <ScrollAnimation variants={fadeInUp}>
              <h3 className="section-title">
                <span className="section-title-text">Plan Your Visit</span>
                <span className="section-title-underline"></span>
              </h3>
            </ScrollAnimation>
            
            {/* Transportation Information Section */}
            <div className="transport-section">
              <motion.div 
                className="transport-grid"
                initial={{ opacity: 0 }}
                whileInView={{ 
                  opacity: 1,
                  transition: { staggerChildren: 0.15, delayChildren: 0.2 }
                }}
                viewport={{ once: true, margin: "-50px" }}
              >
                <motion.div 
                  className="transport-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ 
                    opacity: 1, 
                    y: 0,
                    transition: {
                      duration: 0.5,
                      ease: [0.16, 1, 0.3, 1]
                    }
                  }}
                  viewport={{ once: true }}
                  whileHover={{ 
                    y: -5,
                    boxShadow: "0 10px 25px rgba(212, 175, 55, 0.15)"
                  }}
                >
                  <div className="transport-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
                    </svg>
                  </div>
                  <h4>By Bus</h4>
                  <p>Edinburgh has an extensive bus network operated by Lothian Buses. The main bus terminal is at St Andrew Square. Day tickets cost around £4.50 and offer unlimited travel.</p>
                  <a 
                    href="https://www.lothianbuses.com/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="transport-link"
                    onClick={(e) => {
                      e.preventDefault();
                      window.open("https://www.lothianbuses.com/", "_blank", "noopener,noreferrer");
                    }}
                  >
                    Check Routes
                  </a>
                </motion.div>
                
                <motion.div 
                  className="transport-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ 
                    opacity: 1, 
                    y: 0,
                    transition: {
                      duration: 0.5,
                      ease: [0.16, 1, 0.3, 1]
                    }
                  }}
                  viewport={{ once: true }}
                  whileHover={{ 
                    y: -5,
                    boxShadow: "0 10px 25px rgba(212, 175, 55, 0.15)"
                  }}
                >
                  <div className="transport-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                    </svg>
                  </div>
                  <h4>Walking</h4>
                  <p>Edinburgh is a compact city that's perfect for exploring on foot. Many attractions in the Old Town and New Town are within walking distance of each other. Download a walking map to help navigate.</p>
                  <a 
                    href="https://edinburgh.org/discover/explore/walking/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="transport-link"
                    onClick={(e) => {
                      e.preventDefault();
                      window.open("https://edinburgh.org/discover/explore/walking/", "_blank", "noopener,noreferrer");
                    }}
                  >
                    Walking Routes
                  </a>
                </motion.div>
                
                <motion.div 
                  className="transport-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ 
                    opacity: 1, 
                    y: 0,
                    transition: {
                      duration: 0.5,
                      ease: [0.16, 1, 0.3, 1]
                    }
                  }}
                  viewport={{ once: true }}
                  whileHover={{ 
                    y: -5,
                    boxShadow: "0 10px 25px rgba(212, 175, 55, 0.15)"
                  }}
                >
                  <div className="transport-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                    </svg>
                  </div>
                  <h4>Tram</h4>
                  <p>The Edinburgh Tram connects the airport with the city center. Trams run every 7 minutes during the day and take around 30 minutes from the airport to Princes Street.</p>
                  <a 
                    href="https://edinburghtrams.com/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="transport-link"
                    onClick={(e) => {
                      e.preventDefault();
                      window.open("https://edinburghtrams.com/", "_blank", "noopener,noreferrer");
                    }}
                  >
                    Tram Info
                  </a>
                </motion.div>
                
                <motion.div 
                  className="transport-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ 
                    opacity: 1, 
                    y: 0,
                    transition: {
                      duration: 0.5,
                      ease: [0.16, 1, 0.3, 1]
                    }
                  }}
                  viewport={{ once: true }}
                  whileHover={{ 
                    y: -5,
                    boxShadow: "0 10px 25px rgba(212, 175, 55, 0.15)"
                  }}
                >
                  <div className="transport-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
                    </svg>
                  </div>
                  <h4>Taxi</h4>
                  <p>Black cabs are readily available throughout Edinburgh and can be hailed on the street or found at taxi ranks. Apps like Free Now and Uber also operate in the city.</p>
                  <a 
                    href="https://www.edinburgh.gov.uk/taxis-private-hire-cars" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="transport-link"
                    onClick={(e) => {
                      e.preventDefault();
                      window.open("https://www.edinburgh.gov.uk/taxis-private-hire-cars", "_blank", "noopener,noreferrer");
                    }}
                  >
                    Taxi Info
                  </a>
                </motion.div>
              </motion.div>
              
              <ScrollAnimation variants={fadeInRight} className="transport-map-container">
                <div className="transport-info">
                  <h3>Getting Around Edinburgh</h3>
                  <p>Edinburgh is a compact city with excellent public transport options. Most of the major attractions are located in or near the city center, making it easy to explore on foot or by using public transport.</p>
                  <div className="transport-highlights">
                    <div className="transport-highlight-item">
                      <span className="highlight-icon">🚌</span>
                      <span>Frequent bus services</span>
                    </div>
                    <div className="transport-highlight-item">
                      <span className="highlight-icon">🚶</span>
                      <span>Walkable city center</span>
                    </div>
                    <div className="transport-highlight-item">
                      <span className="highlight-icon">🚋</span>
                      <span>Airport tram connection</span>
                    </div>
                    <div className="transport-highlight-item">
                      <span className="highlight-icon">🚲</span>
                      <span>Bike hire available</span>
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="transport-cta-button"
                    onClick={() => window.open('https://edinburgh.org/discover/getting-here-around/', '_blank')}
                  >
                    Explore Transport Options
                  </motion.button>
                </div>
              </ScrollAnimation>
            </div>
            
            {/* Rest of the interactive content */}
            <div className="action-buttons">
              <Link to="/todo" className="action-button primary">
                Browse Attractions
              </Link>
              <Link to="/calendar" className="action-button secondary">
                Plan Your Trip
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* NEW SECTION: Edinburgh Highlights - to fill the gap at the bottom */}
      <section 
        ref={highlightsRef}
        className={`highlights-section ${visibleSections.highlights ? 'visible' : ''}`}
      >
        <div className="highlights-background"></div>
        <div className="container">
          <ScrollAnimation variants={fadeInUp}>
            <h3 className="section-title">
              <span className="section-title-text">Edinburgh Highlights</span>
              <span className="section-title-underline"></span>
            </h3>
          </ScrollAnimation>
          
          <div className="highlights-intro">
            <ScrollAnimation variants={fadeInUp}>
              <p className="highlights-description">
                Edinburgh is a city of enchanting contrasts. From the medieval Old Town to the elegant Georgian New Town, 
                discover what makes this UNESCO World Heritage site one of Europe's most beautiful cities.
              </p>
            </ScrollAnimation>
          </div>
          
          <motion.div 
            className="highlights-grid"
            initial="hidden"
            variants={staggerContainer}
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
          >
            <ScrollAnimation variants={fadeInLeft} className="highlight-card large">
              <div className="highlight-card-image" style={{backgroundImage: `url(${edinburghCastleImg})`}}>
                <div className="highlight-card-overlay"></div>
                <h4 className="highlight-card-title">Rich Heritage</h4>
              </div>
              <div className="highlight-card-content">
                <p>
                  Edinburgh's streets are steeped in history, with medieval tenements and elegant 
                  Georgian townhouses standing side by side. The iconic Edinburgh Castle watches over 
                  the city from its perch on Castle Rock, dating back to the 12th century.
                </p>
                <Link to="/todo" className="highlight-card-link">Discover Historic Sites</Link>
              </div>
            </ScrollAnimation>
            
            <ScrollAnimation variants={fadeInUp} className="highlight-card">
              <div className="highlight-card-image" style={{backgroundImage: `url(${ghostToursImg})`}}>
                <div className="highlight-card-overlay"></div>
                <h4 className="highlight-card-title">Mysterious Underground</h4>
              </div>
              <div className="highlight-card-content">
                <p>
                  Explore Edinburgh's hidden underground city, where dark vaults and forgotten streets 
                  tell tales of the past. Join a ghost tour to uncover the city's mysterious and haunted history.
                </p>
                <Link to="/todo" className="highlight-card-link">Explore Hidden Edinburgh</Link>
              </div>
            </ScrollAnimation>
            
            <ScrollAnimation variants={fadeInRight} className="highlight-card">
              <div className="highlight-card-image" style={{backgroundImage: `url(${princessStreetGardensImg})`}}>
                <div className="highlight-card-overlay"></div>
                <h4 className="highlight-card-title">Natural Beauty</h4>
              </div>
              <div className="highlight-card-content">
                <p>
                  Despite being a capital city, Edinburgh offers stunning natural landscapes. 
                  From the panoramic views atop Arthur's Seat to the tranquil Princes Street Gardens, 
                  nature is never far away.
                </p>
                <Link to="/todo" className="highlight-card-link">Discover Green Spaces</Link>
              </div>
            </ScrollAnimation>
            
            <ScrollAnimation variants={fadeInLeft} className="highlight-card">
              <div className="highlight-card-image" style={{backgroundImage: `url(${scotchWhiskyExperienceImg})`}}>
                <div className="highlight-card-overlay"></div>
                <h4 className="highlight-card-title">Culinary Delights</h4>
              </div>
              <div className="highlight-card-content">
                <p>
                  Sample traditional Scottish cuisine or dine at one of Edinburgh's Michelin-starred restaurants. 
                  Don't miss the opportunity to taste authentic Scotch whisky at specialist bars and distilleries.
                </p>
                <Link to="/todo" className="highlight-card-link">Taste Edinburgh</Link>
              </div>
            </ScrollAnimation>
            
            <ScrollAnimation variants={fadeInRight} className="highlight-card large">
              <div className="highlight-card-image" style={{backgroundImage: `url(${royalMileImg})`}}>
                <div className="highlight-card-overlay"></div>
                <h4 className="highlight-card-title">Cultural Hub</h4>
              </div>
              <div className="highlight-card-content">
                <p>
                  Edinburgh is world-famous for its festivals, including the Edinburgh Festival Fringe - 
                  the world's largest arts festival. The city is also home to numerous museums, galleries, 
                  and theaters showcasing Scottish culture and international treasures.
                </p>
                <Link to="/calendar" className="highlight-card-link">Check Festival Calendar</Link>
              </div>
            </ScrollAnimation>
          </motion.div>
          
          <ScrollAnimation variants={popIn} className="highlights-cta-container">
            <div className="highlights-cta">
              <h4>Ready to experience Edinburgh?</h4>
              <p>Start planning your unforgettable journey today.</p>
              <div className="highlights-cta-buttons">
                <Link to="/todo" className="cta-button primary">
                  Explore Attractions
                </Link>
                <Link to="/calendar" className="cta-button secondary">
                  Plan Your Visit
                </Link>
              </div>
            </div>
          </ScrollAnimation>
        </div>
      </section>
    </>
  );
};

export default Home;
